import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Loader2, CheckCheck, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact dialog to close a ticket in one shot:
 * generates an AI summary, marks draft quotes as executed, adds the resolved
 * ticket to the KB, and notifies the ticket creator via push notification.
 */
export function CloseWithReportDialog({ open, onOpenChange, ticket, onClosed }) {
  const [summary, setSummary] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [markQuotes, setMarkQuotes] = useState(true);
  const [addToKb, setAddToKb] = useState(true);
  const [notifyCreator, setNotifyCreator] = useState(true);

  useEffect(() => {
    if (open) {
      setSummary("");
      setMarkQuotes(true);
      setAddToKb(true);
      setNotifyCreator(true);
    }
  }, [open]);

  const generateWithAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/preview-report`);
      setSummary(data.summary);
      toast.success("Report generato — modificalo se necessario, poi conferma");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setAiLoading(false); }
  };

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/tickets/${ticket.id}/close-with-report`, {
        resolution_summary: summary || null,
        use_ai: !summary,
        mark_quotes_executed: markQuotes,
        add_to_kb: addToKb,
        notify_creator: notifyCreator,
      });
      const bits = [];
      if (data.kb_entry_id) bits.push("KB aggiornata");
      if (data.quotes_marked_executed) bits.push(`${data.quotes_marked_executed} preventivo/i eseguiti`);
      if (data.push_delivered) bits.push("cliente notificato");
      toast.success("Ticket risolto" + (bits.length ? ` · ${bits.join(" · ")}` : ""));
      onClosed?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight flex items-center gap-2">
            <CheckCheck className="h-5 w-5 text-emerald-600" /> Chiudi con report
          </DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            Concludi l&apos;intervento in un&apos;unica azione: report di risoluzione, preventivi eseguiti, aggiornamento base di conoscenza e notifica al cliente.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs uppercase tracking-widest text-zinc-500">Report di risoluzione</Label>
              <Button size="sm" variant="outline" onClick={generateWithAi} disabled={aiLoading || saving} className="h-7 rounded-sm text-xs" data-testid="close-ai-generate">
                {aiLoading ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Genero...</> : <><Wand2 className="h-3 w-3 mr-1" /> Genera con AI</>}
              </Button>
            </div>
            <Textarea
              data-testid="close-summary"
              rows={10}
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Descrivi cosa è stato fatto (o lascia vuoto e l'AI genererà un report basandosi sulle note dell'intervento)"
              className="rounded-sm font-mono text-xs leading-relaxed"
            />
            {summary && (
              <div className="mt-1 text-[10px] text-zinc-500 flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> {summary.length} caratteri
              </div>
            )}
          </div>

          <div className="rounded-sm border border-zinc-200 p-3 space-y-2">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">Azioni contestuali</div>
            <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="close-mark-quotes-label">
              <Checkbox checked={markQuotes} onCheckedChange={setMarkQuotes} data-testid="close-mark-quotes" className="mt-0.5" />
              <span>
                <span className="text-zinc-900 font-medium">Marca preventivi come eseguiti</span>
                <div className="text-xs text-zinc-500">I ricambi nei preventivi in bozza verranno registrati come utilizzati (alimenta la base di conoscenza).</div>
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="close-add-kb-label">
              <Checkbox checked={addToKb} onCheckedChange={setAddToKb} data-testid="close-add-kb" className="mt-0.5" />
              <span>
                <span className="text-zinc-900 font-medium">Aggiungi alla base di conoscenza</span>
                <div className="text-xs text-zinc-500">Crea (o aggiorna) una entry KB con sintomo, risoluzione e ricambi usati.</div>
              </span>
            </label>
            <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="close-notify-label">
              <Checkbox checked={notifyCreator} onCheckedChange={setNotifyCreator} data-testid="close-notify" className="mt-0.5" />
              <span>
                <span className="text-zinc-900 font-medium">Notifica il richiedente</span>
                <div className="text-xs text-zinc-500">Invia notifica push all&apos;autore del ticket (se ha abilitato le notifiche).</div>
              </span>
            </label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)} disabled={saving}>Annulla</Button>
          <Button
            data-testid="close-submit-btn"
            onClick={submit}
            disabled={saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
          >
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Chiudo...</> : <><CheckCheck className="h-4 w-4 mr-2" /> Chiudi ticket</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
